import { HouseholdType, OpportunityStatus, OpportunityView } from '@ndvr/customer-api';

import { DragEndEvent, KanbanBoard, KanbanCard, KanbanCards, KanbanHeader, KanbanProvider } from '@/components/Kanban';
import { titleCase } from 'utils';

import { useUpdateOpportunityStatus } from '../opportunity-queries';
import { useOpportunityOverlayState } from '../opportunity-state';
import { OpportunityCard } from './OpportunityCard';

interface OpportunityKanbanViewProps {
    opportunities: OpportunityView[];
    householdMap: Map<string, { name: string; type?: HouseholdType }>;
    isLoading?: boolean;
}

const opportunityStatuses = Object.values(OpportunityStatus);

const columns = opportunityStatuses.map((status) => ({
    id: status,
    name: titleCase(status.replace(/_/g, ' ')),
}));

const transformOpportunityToKanbanItem = (opportunity: OpportunityView) => ({
    id: opportunity.opportunityGUID,
    name: opportunity.name || '',
    column: opportunity.status as string,
    opportunity,
});

export const OpportunityKanbanView = (props: OpportunityKanbanViewProps) => {
    const updateOpportunityStatus = useUpdateOpportunityStatus();
    const { openViewOpportunitySheet } = useOpportunityOverlayState();

    if (props.isLoading) {
        return (
            <div className="flex min-w-[1100px] items-center justify-center py-12">
                <div className="text-muted-foreground">Loading opportunities...</div>
            </div>
        );
    }

    const kanbanOpportunities = props.opportunities.map(transformOpportunityToKanbanItem);

    const handleDragEnd = (event: DragEndEvent) => {
        if (!event.over) {
            return;
        }
        const over = event.over;
        const id = event.active.id;
        const opportunity = kanbanOpportunities.find((item) => item.id === id)?.opportunity;
        if (!opportunity) return;

        let newStatus: OpportunityStatus | undefined;

        if (over.data?.current?.type === 'column') {
            newStatus = over.data.current.columnId;
        } else {
            newStatus =
                columns.find((column) => column.id === over.id)?.id ??
                kanbanOpportunities.find((item) => item.id === over.id)?.opportunity?.status;
        }

        if (newStatus && newStatus !== opportunity.status) {
            updateOpportunityStatus.mutate({
                opportunity,
                status: newStatus,
            });
            return;
        }
    };

    return (
        <div className="h-full">
            <KanbanProvider
                columns={columns}
                data={kanbanOpportunities}
                onDragEnd={handleDragEnd}
                className="min-w-[1100px]"
            >
                {(column) => (
                    <KanbanBoard id={column.id} key={column.id} className="border-transparent bg-white">
                        <KanbanHeader className="border-none">
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="font-medium capitalize">{column.name}</h3>
                                <span className="bg-muted text-muted-foreground rounded px-2 py-1 text-xs font-medium">
                                    {kanbanOpportunities.filter((item) => item.column === (column.id as string)).length}
                                </span>
                            </div>
                        </KanbanHeader>
                        <KanbanCards id={column.id}>
                            {(kanbanItem) => (
                                <KanbanCard
                                    className="!border-border-gray !rounded-lg"
                                    column={column.name}
                                    id={kanbanItem.id}
                                    key={kanbanItem.id}
                                    name={kanbanItem.name}
                                >
                                    <OpportunityCard
                                        opportunity={kanbanItem.opportunity as OpportunityView}
                                        householdMap={props.householdMap}
                                        onTitleClick={(opportunity) =>
                                            openViewOpportunitySheet(opportunity.opportunityGUID)
                                        }
                                    />
                                </KanbanCard>
                            )}
                        </KanbanCards>
                    </KanbanBoard>
                )}
            </KanbanProvider>
        </div>
    );
};
